import axios from "@shared/axios";
import User from "@shared-models/profile/user.model";
import UserSubscription from "@shared-models/shop/user.subscription.model";
import WorkoutType from "@shared-models/planning/workout.type.model";
import StripeSubscriptionInvoiceDTO from "@shared-dto/shop/stripe.subscription.invoice.dto";

async function getUserSubscriptionById(userSubscriptionId: string): Promise<UserSubscription>
{
	const response = await axios.get(
		`/admin/shop/user-subscriptions/${ userSubscriptionId }`
	);

	return response.data as UserSubscription ?? null;
}

async function getUserSubscriptionsByUser(user: User): Promise<UserSubscription[]>
{
	const response = await axios.get(
		`/admin/users/${ user.id }/user-subscriptions`,
	);

	return response?.data as UserSubscription[] ?? null;
}

async function getActiveUserSubscriptionsByUser(user: User): Promise<UserSubscription[]>
{
	const response = await axios.get(
		`/admin/users/${ user.id }/user-subscriptions/active`,
	);

	return response?.data as UserSubscription[] ?? [];
}

async function getUsersSubscriptions(statusFilter?: string | null, paymentTypeFilter?: string | null, subscriptionId?: string | null): Promise<Record<string, UserSubscription[]>>
{
	const response = await axios.get(
		"/admin/shop/user-subscriptions",
		{
			params: {
				status: statusFilter,
				paymentType: paymentTypeFilter,
				subscription_id: subscriptionId
			}
		}
	);

	return response.data ?? {};
}

async function getUsersSubscriptionsCounts()
{
	const response = await axios.get(
		"/admin/shop/user-subscriptions/counts"
	);

	return response?.data ?? [];
}

async function getInvoicesByUserSubscription(userSubscription: UserSubscription): Promise<StripeSubscriptionInvoiceDTO[]>
{
	const response = await axios.get(
		`/admin/shop/user-subscriptions/${ userSubscription?.id ?? "-" }/invoices`
	);

	return response?.data as StripeSubscriptionInvoiceDTO[] ?? [];
}

async function getNextInvoiceByUserSubscription(userSubscription: UserSubscription): Promise<StripeSubscriptionInvoiceDTO>
{
	const response = await axios.get(
		`/admin/shop/user-subscriptions/${ userSubscription?.id ?? "-" }/next-invoice`
	);

	return response?.data as StripeSubscriptionInvoiceDTO ?? null;
}

async function updateUserSubscription(userSubscription: UserSubscription): Promise<UserSubscription>
{
	if ("-1" == userSubscription.id)
	{
		const response = await axios.post(
			"/admin/shop/user-subscriptions",
			{
				"user": userSubscription.user?.id ?? userSubscription.user,
				"subscription": userSubscription.subscription?.id,
				"status": userSubscription.status,
				"amount": userSubscription.amount,
				"start_at": userSubscription.startAt,
				"end_at": userSubscription.endAt,
				"commitment_period_end_at": userSubscription.commitmentPeriodEndAt,
				"validity_end_at": userSubscription.validityEndAt,
				"canceled_at": userSubscription.canceledAt,
				"subscription_paused_at": userSubscription.subscriptionPausedAt,
				"stripe_subscription_id": userSubscription.stripeSubscriptionId,
				"stripe_payment_intent_id": userSubscription.stripePaymentIntentId,
				"client_type": userSubscription.clientType,
				"visitor_firstname": userSubscription.visitorFirstname,
				"visitor_lastname": userSubscription.visitorLastname,
				"visitor_email": userSubscription.visitorEmail
			}
		);

		return response?.data as UserSubscription;
	}
	else
	{
		const response = await axios.put(
			`/admin/shop/user-subscriptions/${ userSubscription.id }`,
			{
				"user": userSubscription.user?.id,
				"subscription": userSubscription.subscription?.id,
				"status": userSubscription.status,
				"amount": userSubscription.amount,
				"start_at": userSubscription.startAtIso ?? userSubscription.startAt,
				"end_at": userSubscription.endAt,
				"commitment_period_end_at": userSubscription.commitmentPeriodEndAt,
				"validity_end_at": userSubscription.validityEndAt,
				"canceled_at": userSubscription.canceledAt,
				"subscription_paused_at": userSubscription.subscriptionPausedAt,
				"stripe_subscription_id": userSubscription.stripeSubscriptionId,
				"stripe_payment_intent_id": userSubscription.stripePaymentIntentId,
			}
		);

		return response?.data as UserSubscription;
	}
}

async function deleteUserSubscription(userSubscriptionToDelete: UserSubscription)
{
	const response = await axios.delete(
		`/admin/shop/user-subscriptions/${ userSubscriptionToDelete.id }`,
	);

	return response?.data ?? null;
}

async function cancelUserSubscription(userSubscriptionToCancel: UserSubscription)
{
	const response = await axios.put(
		`/admin/shop/user-subscriptions/${ userSubscriptionToCancel.id }/cancel`,
	);

	return response?.data ?? null;
}

async function getUsersSubscriptionsBySubscription(subscriptionId: string): Promise<UserSubscription[]>
{
	const response = await axios.get(
		"/admin/shop/user-subscriptions",
		{
			params: {
				subscription_id: subscriptionId
			}
		}
	);

	return response?.data as UserSubscription[] ?? [];
}

async function updateUserSubscriptionAllowedWorkoutType(userSubscription: UserSubscription, workoutType: WorkoutType): Promise<string>
{
	const response = await axios.put(
		`/admin/shop/user-subscriptions/${ userSubscription.id }/workout-types`,
		{
			"workout_type": workoutType.id
		}
	);

	return response?.data ?? null;
}

export {
	getUserSubscriptionById,
	getUserSubscriptionsByUser,
	getActiveUserSubscriptionsByUser,
	getUsersSubscriptions,
	getUsersSubscriptionsBySubscription,
	getUsersSubscriptionsCounts,
	getInvoicesByUserSubscription,
	getNextInvoiceByUserSubscription,

	updateUserSubscription,
	updateUserSubscriptionAllowedWorkoutType,
	deleteUserSubscription,
	cancelUserSubscription
};
